import {
  castArray,
  compact,
  isArray,
  isObject,
  isString,
  map,
  split,
} from 'lodash';
import OddsConverter from '@/services/helpers/odds-converter';

export const PERCENTAGE_STEP = 1;
export const PERCENTAGE_MAX = 100;

export const SELECTION_OUTCOME_CLASSES = {
  WINNER: 'win',
  LOSER: 'loss',
  VOIDED: 'void',
  PUSHED: 'push',
  NOT_YET_SETTLED: '',
};

export const getSelectionResultingClass = (outcome) => (
  SELECTION_OUTCOME_CLASSES[outcome] || ''
);

export const createEmptySelection = () => ({
  name: '',
  probability: '',
  margin: '',
  calculatedPrice: null,
  isPriceLoading: false,
});

export const isValidProbabilityPercentage = (value) => {
  if (value === '' || value == null) return false;
  const num = Number(value);
  return Number.isFinite(num) && num > 0 && num <= PERCENTAGE_MAX;
};

export const isValidMarginPercentage = (value) => {
  if (value === '' || value == null) return false;
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 && num <= PERCENTAGE_MAX;
};

export const toPercentage = (value) => Number(value) / 100;

export const AMERICAN_PRICE_TYPE = 'american';

export const buildSelectionPricePayload = (selection) => {
  const { originalFormattedValue } = selection.calculatedPrice || {};

  return {
    probability: toPercentage(selection.probability),
    margin: toPercentage(selection.margin),
    originalFormattedValue: {
      priceType: AMERICAN_PRICE_TYPE,
      ...(originalFormattedValue?.value != null ? {
        value: originalFormattedValue.value,
        signIsPlus: originalFormattedValue.signIsPlus,
      } : {}),
    },
  };
};

export const toDisplayPercentage = (value) => {
  if (value == null || value === '') return '';
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  return String(parseFloat((num * 100).toFixed(2)));
};

export const parseCalculatedPrice = (response) => {
  const data = response?.data ?? response;
  if (!data) return null;

  const originalFormattedValue = data.originalFormattedValue ?? (
    data.priceType === AMERICAN_PRICE_TYPE && data.value != null
      ? {
        priceType: AMERICAN_PRICE_TYPE,
        value: Number(data.value),
        signIsPlus: data.signIsPlus,
      }
      : null
  );

  if (originalFormattedValue?.value == null || !Number.isFinite(Number(originalFormattedValue.value))) {
    return null;
  }

  return { originalFormattedValue };
};

export const buildSelectionPriceObject = (calculatedPrice, selection) => {
  const { originalFormattedValue } = calculatedPrice || {};
  if (originalFormattedValue?.value == null) return null;

  return {
    probability: toPercentage(selection.probability),
    margin: toPercentage(selection.margin),
    bias: 0,
    originalFormattedValue,
  };
};

export const getDecimalValueFromOriginalFormattedValue = (originalFormattedValue) => {
  const { priceType, value, signIsPlus } = originalFormattedValue || {};
  const numValue = Number(value);
  if (value == null || !Number.isFinite(numValue)) return null;

  switch (priceType) {
  case 'decimal':
    return numValue;
  case 'american':
    return signIsPlus
      ? 1 + numValue / 100
      : 1 + 100 / Math.abs(numValue);
  case 'probability':
    return numValue > 0 ? 1 / numValue : null;
  case 'fractional': {
    const [numerator, denominator] = map(split(String(value), '/'), Number);
    if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || !denominator) return null;
    return 1 + numerator / denominator;
  }
  default:
    return null;
  }
};

const formatMatchingOriginalFormattedValue = (originalFormattedValue, oddFormat) => {
  const { value, signIsPlus, priceType } = originalFormattedValue;
  if (priceType !== oddFormat || value == null) return null;

  if (oddFormat === 'american') {
    return `${signIsPlus ? '+' : '-'}${value}`;
  }
  if (oddFormat === 'decimal') {
    return parseFloat(value).toFixed(2);
  }
  if (oddFormat === 'fractional') {
    return String(value);
  }
  if (oddFormat === 'probability') {
    const numValue = Number(value);
    const percentage = numValue <= 1 ? numValue * 100 : numValue;
    const formatted = parseFloat(percentage.toFixed(1));
    return (formatted % 1 > 0) ? `${formatted}%` : `${Math.round(formatted)}%`;
  }
  return String(value);
};

export const formatStoredPriceDisplay = (price, oddFormat) => {
  if (!price) return '';

  const { originalFormattedValue } = price;
  const matchingDisplay = originalFormattedValue?.value != null
    ? formatMatchingOriginalFormattedValue(originalFormattedValue, oddFormat)
    : null;

  if (matchingDisplay != null) return matchingDisplay;

  if (oddFormat === 'probability') {
    const probability = parseFloat(((price.probability ?? 0) + (price.bias ?? 0)) * 100).toFixed(1);
    return (probability % 1 > 0) ? `${probability}%` : `${Math.round(probability)}%`;
  }

  const decimalValue = getDecimalValueFromOriginalFormattedValue(originalFormattedValue);

  if (decimalValue == null || !Number.isFinite(Number(decimalValue))) return '';

  return OddsConverter.getMappedOddValue({
    decimalValue: Number(decimalValue),
    probability: price.probability ?? 0,
    margin: price.margin ?? 0,
    bias: price.bias ?? 0,
    originalFormattedValue,
  }, oddFormat);
};

export const formatSelectionPriceDisplay = (calculatedPrice, selection, oddFormat) => {
  const price = buildSelectionPriceObject(calculatedPrice, selection);
  if (!price) return '';
  return formatStoredPriceDisplay(price, oddFormat);
};

export const parseSelectionsFromApi = (raw) => {
  if (raw == null || raw === '') return [];

  let parsed = raw;
  if (isString(raw)) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }

  if (!isArray(parsed)) return [];

  return map(parsed, (selection) => {
    const price = selection?.price || {};
    const calculatedPrice = price.originalFormattedValue?.value != null
      ? { originalFormattedValue: price.originalFormattedValue }
      : null;

    return {
      selectionId: selection.selectionId || '',
      name: selection.selectionName || selection.name || '',
      offeringStatus: selection.offeringStatus || '',
      displayStatus: selection.displayStatus || '',
      outcome: selection.outcome || 'NOT_YET_SETTLED',
      resultingStatus: selection.resultingStatus || '',
      probability: toDisplayPercentage(price.probability),
      margin: toDisplayPercentage(price.margin),
      calculatedPrice,
      storedPrice: price,
      isPriceLoading: false,
    };
  });
};

export const toSelectItems = (nodes, idKey, nameKey) => map(nodes || [], (node) => ({
  value: node[idKey],
  label: node[nameKey],
}));

export const toIdSelectItems = (ids, labelMap = {}) => map(compact(ids || []), (id) => ({
  value: id,
  label: labelMap[id] || id,
}));

export const parseIdList = (raw) => {
  if (raw == null || raw === '') return [];
  if (isArray(raw)) return raw;
  if (isString(raw)) {
    try {
      const parsed = JSON.parse(raw);
      return isArray(parsed) ? parsed : castArray(raw);
    } catch {
      return castArray(raw);
    }
  }
  return [];
};

export const parseJsonObject = (raw) => {
  if (raw == null || raw === '') return {};
  if (isObject(raw) && !isArray(raw)) return raw;
  if (isString(raw)) {
    try {
      const parsed = JSON.parse(raw);
      return isObject(parsed) && !isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};
